import Phaser from 'phaser';
import { TIERS, getTierScale } from './shapes';
import { createShape, ShapeBody } from './shapeFactory';

interface MergeEvent {
  tierIndex: number;
  sourceTierIndex: number;
  newBody: ShapeBody | null;
  bonus: number;
  x: number;
  y: number;
}

/**
 * Listens to Matter collisions and merges same-tier shapes at their midpoint
 * with averaged velocity. Highest-tier collisions remove both with a bonus.
 */
export class MergeManager {
  private scene: Phaser.Scene;
  private mergingIds = new Set<number>();
  onMerge?: (event: MergeEvent) => void;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    scene.matter.world.on('collisionstart', this.onCollision, this);
  }

  destroy(): void {
    this.scene.matter.world.off('collisionstart', this.onCollision, this);
  }

  private onCollision(event: Phaser.Physics.Matter.Events.CollisionStartEvent): void {
    for (const pair of event.pairs) {
      const aGo = (pair.bodyA as MatterJS.BodyType).gameObject as ShapeBody | undefined;
      const bGo = (pair.bodyB as MatterJS.BodyType).gameObject as ShapeBody | undefined;
      if (!aGo || !bGo) continue;
      if (aGo.merging || bGo.merging) continue;
      if (this.mergingIds.has(aGo.shapeId) || this.mergingIds.has(bGo.shapeId)) continue;
      if (aGo.shapeTier !== bGo.shapeTier) continue;
      this.mergePair(aGo, bGo);
    }
  }

  private mergePair(a: ShapeBody, b: ShapeBody): void {
    a.merging = true;
    b.merging = true;
    this.mergingIds.add(a.shapeId);
    this.mergingIds.add(b.shapeId);

    const tierIndex = a.shapeTier;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const va = a.body as MatterJS.BodyType;
    const vb = b.body as MatterJS.BodyType;
    const vx = (va.velocity.x + vb.velocity.x) / 2;
    const vy = (va.velocity.y + vb.velocity.y) / 2;
    const color = TIERS[tierIndex].color;

    // Destroy the merging pair IMMEDIATELY. Tweening scale on a Matter.Image
    // mutates the physics body, which races against the destroy at tween end
    // (especially with rapid-fire merges). Pop effect runs on a separate
    // non-physics Graphics so it can't touch the Matter world.
    a.destroy();
    b.destroy();
    this.spawnPop(mx, my, color, TIERS[tierIndex].radius * getTierScale(), tierIndex);

    const nextTierIndex = tierIndex + 1;

    if (nextTierIndex >= TIERS.length) {
      this.onMerge?.({ tierIndex, sourceTierIndex: tierIndex, newBody: null, bonus: 500, x: mx, y: my });
      return;
    }

    this.scene.time.delayedCall(40, () => {
      const merged = createShape(this.scene, nextTierIndex, mx, my);
      merged.setVelocity(vx, vy);
      merged.setDepth(15);
      this.punchScale(merged, TIERS[nextTierIndex].radius * getTierScale(), nextTierIndex);
      this.onMerge?.({ tierIndex: nextTierIndex, sourceTierIndex: tierIndex, newBody: merged, bonus: 0, x: mx, y: my });
    });
  }

  private spawnPop(x: number, y: number, color: number, radius: number, tierIndex: number): void {
    const t = Math.min(tierIndex / 9, 1); // 0 (tiny) → 1 (max tier)

    // Inner soft disc — expands and fades.
    const disc = this.scene.add.graphics({ x, y });
    disc.setDepth(25);
    disc.fillStyle(color, 0.7);
    disc.fillCircle(0, 0, radius);
    this.scene.tweens.add({
      targets: disc,
      scale: 2.2 + t * 1.2,
      alpha: 0,
      duration: 440,
      ease: 'Cubic.easeOut',
      onComplete: () => disc.destroy(),
    });

    // White core flash (tier 2+) — brief bright center punch.
    if (tierIndex >= 2) {
      const flash = this.scene.add.graphics({ x, y });
      flash.setDepth(28);
      flash.fillStyle(0xffffff, 0.9);
      flash.fillCircle(0, 0, radius * 0.6);
      this.scene.tweens.add({
        targets: flash,
        scale: 1.4,
        alpha: 0,
        duration: 160 + t * 60,
        ease: 'Cubic.easeOut',
        onComplete: () => flash.destroy(),
      });
    }

    // Outer ring shockwave.
    const lineW = 2.5 + t * 3.5;
    const ring = this.scene.add.graphics({ x, y });
    ring.setDepth(26);
    ring.lineStyle(lineW, color, 0.9);
    ring.strokeCircle(0, 0, radius);
    this.scene.tweens.add({
      targets: ring,
      scale: 3 + t * 1.5,
      alpha: 0,
      duration: 640 + t * 220,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Second delayed ring (tier 4+) — double-pulse echo.
    if (tierIndex >= 4) {
      const ring2 = this.scene.add.graphics({ x, y });
      ring2.setDepth(26);
      ring2.lineStyle(lineW * 0.7, color, 0.6);
      ring2.strokeCircle(0, 0, radius);
      this.scene.time.delayedCall(120, () => {
        this.scene.tweens.add({
          targets: ring2,
          scale: 2.5 + t,
          alpha: 0,
          duration: 500,
          ease: 'Cubic.easeOut',
          onComplete: () => ring2.destroy(),
        });
      });
    }

    // Chroma ring (tier 6+) — large warm glow ring for big merges.
    if (tierIndex >= 6) {
      const chroma = this.scene.add.graphics({ x, y });
      chroma.setDepth(24);
      chroma.lineStyle(8 + t * 6, 0xfff0a0, 0.35);
      chroma.strokeCircle(0, 0, radius);
      this.scene.tweens.add({
        targets: chroma,
        scale: 5.5 + t * 2.5,
        alpha: 0,
        duration: 840 + t * 160,
        ease: 'Cubic.easeOut',
        onComplete: () => chroma.destroy(),
      });
    }

    // Particle sparks — count and spread scale with tier.
    const sparkCount = Math.round(10 + t * 20);
    const sparkColors = [color, 0xffffff, 0xfff8d0];
    for (let i = 0; i < sparkCount; i++) {
      const a = (i / sparkCount) * Math.PI * 2 + Math.random() * 0.5;
      const dist = radius * (2.2 + t * 1.8) + Math.random() * 24;
      const isBigEmber = tierIndex >= 5 && Math.random() < 0.35;
      const sparkR = isBigEmber ? 4.5 + Math.random() * 3.5 : 2.5 + Math.random() * 2;
      const sparkColor = sparkColors[Math.floor(Math.random() * sparkColors.length)];
      const spark = this.scene.add.graphics({ x, y });
      spark.setDepth(27);
      spark.fillStyle(sparkColor, 0.95);
      spark.fillCircle(0, 0, sparkR);
      this.scene.tweens.add({
        targets: spark,
        x: x + Math.cos(a) * dist,
        y: y + Math.sin(a) * dist,
        alpha: 0,
        scale: 0.2,
        duration: 620 + Math.random() * 320,
        ease: 'Cubic.easeOut',
        onComplete: () => spark.destroy(),
      });
    }

    // Starburst streaks (tier 5+) — thin lines shooting outward.
    if (tierIndex >= 5) {
      const streakCount = Math.round(6 + t * 8);
      for (let i = 0; i < streakCount; i++) {
        const a = (i / streakCount) * Math.PI * 2 + Math.random() * 0.3;
        const len = radius * (1.5 + t * 1.5) + Math.random() * 20;
        const streak = this.scene.add.graphics({ x, y });
        streak.setDepth(26);
        streak.lineStyle(1.5 + t, color, 0.85);
        streak.lineBetween(0, 0, Math.cos(a) * radius * 0.3, Math.sin(a) * radius * 0.3);
        this.scene.tweens.add({
          targets: streak,
          x: x + Math.cos(a) * len,
          y: y + Math.sin(a) * len,
          alpha: 0,
          duration: 420 + Math.random() * 160,
          ease: 'Quad.easeOut',
          onComplete: () => streak.destroy(),
        });
      }
    }

    // Screen flash overlay (tier 7+) — brief full-screen white pulse.
    if (tierIndex >= 7) {
      const { width, height } = this.scene.scale;
      const flash = this.scene.add.graphics().setDepth(50);
      flash.fillStyle(0xffffff, 0.18 + t * 0.12);
      flash.fillRect(0, 0, width, height);
      this.scene.tweens.add({
        targets: flash,
        alpha: 0,
        duration: 220 + t * 80,
        ease: 'Cubic.easeOut',
        onComplete: () => flash.destroy(),
      });
    }
  }

  private punchScale(body: ShapeBody, _baseRadius: number, tierIndex = 0): void {
    // Ghost sprite instead of tweening body.scale directly — Matter.Image.setScale
    // mutates physics body vertices, which races against destroy in rapid merges.
    const t = Math.min(tierIndex / 9, 1);
    const key = body.texture?.key;
    if (!key) return;
    const ghost = this.scene.add.image(body.x, body.y, key).setDepth(16);
    // All pop scales ride the live sprite's scale so the effect lands at the
    // body's true size on tablet/desktop (tier scale 1.32), not unit scale.
    const s = body.scaleX;
    ghost.setScale(s * (0.38 + t * 0.04));
    const follow = () => {
      if (!ghost.active) return;
      if (body.active) ghost.setPosition(body.x, body.y);
    };
    this.scene.events.on('update', follow);
    this.scene.tweens.add({
      targets: ghost,
      scale: s * (1.18 + t * 0.27),
      duration: 200 + t * 80,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.scene.tweens.add({
          targets: ghost,
          scale: s * (1 + t * 0.04),
          alpha: 0,
          duration: 280,
          ease: 'Cubic.easeOut',
          onComplete: () => {
            this.scene.events.off('update', follow);
            ghost.destroy();
          },
        });
      },
    });
  }
}
